import React from 'react';
import { BrowserRouter, Routes, Route, Navigate, Link, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './src/utils/auth.jsx';
import { SearchProvider, useSearch } from './src/utils/searchStorage.jsx';
import { usePreviewAccess } from './src/utils/previewFlags.jsx';
import { useState, useEffect } from 'react';

import LaunchGate from './src/components/LaunchGate.jsx';
import Navbar from './src/components/Navbar.jsx';
import Dashboard from './src/components/Dashboard.jsx';
import SearchSection from './src/components/SearchSection.jsx';
import Profile from './src/components/ProfilePage.jsx';
import SettingsPage from './src/components/SettingsPage.jsx';
import LoginPage from './src/components/LoginPage.jsx';
import RegisterPage from './src/components/RegisterPage.jsx';
import ProtectedRoute from './src/components/ProtectedRoute.jsx';
import TheShowPage from './src/components/TheShowPage.jsx';
import UsageDashboard from './src/components/UsageDashboard.jsx';
import BillingSettings from './src/components/BillingSettings.jsx';
import AdminDashboardPage from './src/components/AdminDashboardPage.jsx';
import ClientAnalyticsDashboard from './src/components/ClientAnalyticsDashboard.jsx';
import ReportDetailPage from './src/components/ReportDetailPage.jsx';
import AnalyticsPage from './src/components/AnalyticsPage.jsx';
import PublicFeed from './src/components/PublicFeed.jsx';
import LandingPage from './src/components/LandingPage.jsx';
import PricingPage from './src/components/PricingPage.jsx';
import LegalPage from './src/components/LegalPage.jsx';

// Import new pages
import About from './src/components/About.jsx';
import Contact from './src/components/Contact.jsx';
import Help from './src/components/Help.jsx';
import Status from './src/components/Status.jsx';
import APIComingSoon from './src/components/APIComingSoon.jsx';
import Footer from './src/components/Footer.jsx';

const EnhancedPublicFeed = PublicFeed;
const EnhancedDashboard = Dashboard;

const ReviewsPage = () => <Dashboard initialView="reviews" />;
const FreeSearchPage = () => <SearchSection limitedAccess={true} searchLimit={10} />;

// Component to conditionally show footer based on route
function AppContent() {
  const location = useLocation();
  
  // Define routes that should have a footer (public/marketing pages)
  const footerRoutes = [
    '/landing',
    '/feed',
    '/pricing', 
    '/browse',
    '/about',
    '/contact',
    '/help',
    '/status',
    '/privacy',
    '/terms',
    '/api-docs'
  ];
  
  // Define routes that should NOT have a footer (app/dashboard pages)
  const noFooterRoutes = [
    '/dashboard',
    '/reviews',
    '/profile',
    '/settings',
    '/search/pro',
    '/usage',
    '/billing',
    '/report',
    '/admin-dashboard',
    '/admin-analytics',
    '/login',
    '/register'
  ];
  
  // Check if current route should show footer
  const shouldShowFooter = footerRoutes.includes(location.pathname) || 
    (!noFooterRoutes.some(route => location.pathname.startsWith(route)) && 
     location.pathname !== '/' && // Don't show on authenticated home
     !location.pathname.startsWith('/search')); // Don't show on search pages

  return shouldShowFooter ? <Footer /> : null;
}

const HomeRoute = () => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-950 via-slate-900 to-slate-800">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-blue-400/30 border-t-blue-400 rounded-full animate-spin mx-auto mb-4"></div>
          <h3 className="text-2xl font-semibold text-white mb-2">Loading CodeRated...</h3>
          <p className="text-slate-400">Initializing your experience</p>
        </div>
      </div>
    );
  }

  if (user) return <Dashboard />;
  return <Navigate to="/landing" replace />;
};

// Applies data-product="coderated" to <html> for allowlisted viewers only,
// which is what activates the Colectiv design system in styles/global.css.
// Everyone else never matches those rules and keeps the shipped look.
//
// Set on <html> rather than a React-owned div so it also covers the page
// background — <body>'s gradient paints outside the app root, and gating on a
// nested element would leave the old indigo field behind the new palette.
//
// Must live INSIDE AuthProvider: usePreviewAccess() reads the auth context.
const PreviewTheme = () => {
  const enabled = usePreviewAccess();

  useEffect(() => {
    const root = document.documentElement;
    if (enabled) root.setAttribute('data-product', 'coderated');
    else root.removeAttribute('data-product');
    // Clean up on unmount so a logout drops the preview without a reload.
    return () => root.removeAttribute('data-product');
  }, [enabled]);

  return null;
};

const App = () => {
  // Add these state variables at the top of the App component
  const [showLegal, setShowLegal] = useState(false);
  const [legalTab, setLegalTab] = useState('terms');

  return (
    <AuthProvider>
      <PreviewTheme />
      <SearchProvider>
        <BrowserRouter>
          <LaunchGate>
          <div className="app min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-800 flex flex-col">
            <Navbar />

            {/* Background Effects */}
            <div className="background-effects">
              <div className="background-grid"></div>
              <div className="floating-orbs">
                <div className="orb"></div>
                <div className="orb"></div>
                <div className="orb"></div>
              </div>
            </div>

            {/* Main Content */}
            <main className="main-content flex-grow">
              <Routes>
                {/* Public Routes */}
                <Route path="/" element={<HomeRoute />} />
                <Route path="/landing" element={<LandingPage />} />
                <Route path="/feed" element={<PublicFeed />} />
                <Route path="/pricing" element={<PricingPage />} />
                <Route path="/login" element={<LoginPage />} />
                <Route path="/register" element={<RegisterPage />} />
                <Route path="/search" element={<FreeSearchPage />} />
                <Route path="/browse" element={<PublicFeed showMoreDetails={true} />} />
                <Route path="/show" element={<TheShowPage />} />
                
                {/* New Public Pages */}
                <Route path="/about" element={<About />} />
                <Route path="/contact" element={<Contact />} />
                <Route path="/help" element={<Help />} />
                <Route path="/status" element={<Status />} />
                <Route path="/api-docs" element={<APIComingSoon />} />
                
                {/* Legal Pages */}
                <Route path="/privacy" element={<LegalPage type="privacy" />} />
                <Route path="/terms" element={<LegalPage type="terms" />} />

                {/* Admin Routes */}
                <Route path="/admin-dashboard" element={
                  <ProtectedRoute>
                    <AdminDashboardPage />
                  </ProtectedRoute>
                } />

                <Route path="/admin-analytics" element={
                  <ProtectedRoute>
                    <AnalyticsPage />
                  </ProtectedRoute>
                } />

                {/* Protected User Routes */}
                <Route path="/dashboard" element={
                  <ProtectedRoute>
                    <Dashboard />
                  </ProtectedRoute>
                } />

                <Route path="/reviews" element={
                  <ProtectedRoute>
                    <ReviewsPage />
                  </ProtectedRoute>
                } />

                <Route path="/profile" element={
                  <ProtectedRoute>
                    <Profile />
                  </ProtectedRoute>
                } />

                <Route path="/settings" element={
                  <ProtectedRoute>
                    <SettingsPage />
                  </ProtectedRoute>
                } />

                <Route path="/search/pro" element={
                  <ProtectedRoute>
                    <SearchSection limitedAccess={false} />
                  </ProtectedRoute>
                } />

                <Route path="/analytics" element={
                  <ProtectedRoute>
                    <ClientAnalyticsDashboard />
                  </ProtectedRoute>
                } />  

                <Route path="/usage" element={
                  <ProtectedRoute>
                    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-800 pt-24 px-4">
                      <div className="max-w-7xl mx-auto">
                        <UsageDashboard />
                      </div>
                    </div>
                  </ProtectedRoute>
                } />

                <Route path="/billing" element={
                  <ProtectedRoute>
                    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-800 pt-24 px-4">
                      <div className="max-w-4xl mx-auto">
                        <BillingSettings />
                      </div>
                    </div>
                  </ProtectedRoute>
                } />

                <Route path="/report/:id" element={
                  <ProtectedRoute>
                    <ReportDetailPage />
                  </ProtectedRoute>
                } />

                {/* 404 */}
                <Route path="*" element={
                  <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-950 via-slate-900 to-slate-800">
                    <div className="text-center">
                      <h1 className="text-6xl font-bold text-white mb-4">404</h1>
                      <p className="text-xl text-slate-400 mb-8">Page not found</p>
                      <Link
                        to="/"
                        className="block bg-gradient-to-r from-blue-500 to-purple-600 text-white px-6 py-3 rounded-lg font-semibold hover:from-blue-600 hover:to-purple-700 transition-all"
                      >
                        Go Home
                      </Link>
                    </div>
                  </div>
                } />
              </Routes>
            </main>

            {/* Conditional Footer */}
            <AppContent />
          </div>
          </LaunchGate>
        </BrowserRouter>
      </SearchProvider>
    </AuthProvider>
  );
};

export default App;